/**
 * CreateMediaPlanPage
 *
 * Purpose: Page component for creating a new media plan using the template-driven
 *          form pattern with external Cancel/Create buttons
 * Architecture: Uses TemplateForm with a ref handle (TemplateFormHandle) for external
 *               button control. Wraps in SkeletonPage for consistent layout with
 *               breadcrumbs. Calls planningService.createPlan on submit, navigates
 *               on success stays on this page so the planner can keep using Maestro
 *               and the form (no redirect). Maestro can switch the left column to
 *               flight timeline + media mixer when ui_actions / timeline API return.
 * Dependencies: React (useRef, useState), react-router-dom (useNavigate),
 *               MUI (Box, Button, Alert, CircularProgress),
 *               components/layout/SkeletonPage, components/template-form (TemplateForm,
 *               TemplateFormHandle, FormData), templates/planning/CreateMediaPlanTemplate,
 *               services/planningService, context/AuthContext (useAuth)
 * Author: Development Team
 * Created: 2026-02-15
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import SkeletonPage from '../../components/layout/SkeletonPage';
import MaestroChatPanel, {
  MaestroChatPanelHandle,
  type MaestroFieldResult,
} from '../../components/chat/MaestroChatPanel';
import FlightTimelinePanel from '../../components/planning/FlightTimelinePanel';
import { TemplateForm } from '../../components/template-form';
import type { TemplateFormHandle, FormData, IndicatorMode } from '../../components/template-form';
import { CreateMediaPlanTemplate } from '../../templates/planning/CreateMediaPlanTemplate';
import { planningService } from '../../services/planningService';
import { planningDraftService } from '../../services/planningDraftService';
import { useAuth } from '../../context/AuthContext';
import type { FlightTimelineBuildResponse } from '../../types/chat';
import { normalizeChannelPercents } from '../../utils/flightTimelineMerge';
import { SecondaryButton } from '../../components/common/ActionButtons';

type WorkspacePhase = 'form' | 'flights';

const CreateMediaPlanPage: React.FC = () => {
  const { state } = useAuth();
  const tenantId = state.user?.activeTenant?.tenant_id || 'GLOBAL';
  const navigate = useNavigate();
  const formRef = useRef<TemplateFormHandle>(null);
  const maestroRef = useRef<MaestroChatPanelHandle>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planSaved, setPlanSaved] = useState(false);
  const [showPlanSavedAck, setShowPlanSavedAck] = useState(false);
  const [entityId] = useState(() => crypto.randomUUID());
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [workspacePhase, setWorkspacePhase] = useState<WorkspacePhase>('form');
  const [flightTimeline, setFlightTimeline] = useState<FlightTimelineBuildResponse | null>(null);
  // Maestro field indicator state (ORCH-266):
  // 'none'            — initial page load; no field shows any border color.
  // 'maestro-pending' — brief attached, Maestro hasn't returned anything yet; only
  //                     fields that already have a value (e.g. the brief field itself)
  //                     show color — required-and-blank stays uncolored so the form
  //                     doesn't flash red before Maestro has had a chance to look.
  // 'maestro'         — Maestro has returned real output; full coloring, including red
  //                     for required-and-empty.
  // 'submitted'       — no-brief path; required-and-empty shows red after a submit attempt.
  // Replaces the old briefProcessed boolean that initialised to true (caused Bug 3).
  const [indicatorMode, setIndicatorMode] = useState<IndicatorMode>('none');
  const [appliedFieldKeys, setAppliedFieldKeys] = useState<Set<string>>(() => new Set());
  const [fieldResults, setFieldResults] = useState<Record<string, MaestroFieldResult> | undefined>(
    undefined
  );

  const [planMetadata, setPlanMetadata] = useState<Record<string, unknown>>({});

  const hasAttachedMediaBrief = useMemo(() => {
    const v = planMetadata['fd_media-brief'];
    if (v === true) return true;
    if (v && typeof v === 'object' && 'has_files' in v) {
      return Boolean((v as { has_files?: boolean }).has_files);
    }
    return false;
  }, [planMetadata]);

  // Attaching a brief starts the Maestro path, but Maestro hasn't returned anything
  // yet — go to 'maestro-pending' so the brief field itself can show green (it has a
  // value) without the rest of the form flashing red before Maestro has had a chance
  // to say what it did or didn't find (ORCH-266 — reported after the last deploy).
  useEffect(() => {
    if (hasAttachedMediaBrief) {
      setIndicatorMode((prev) => (prev === 'none' ? 'maestro-pending' : prev));
    }
  }, [hasAttachedMediaBrief]);

  const handleApplyCreatePlanFields = useCallback(
    (fields: Record<string, unknown>, newFieldResults?: Record<string, MaestroFieldResult>) => {
      formRef.current?.applyAssistantDraft(fields);
      setAppliedFieldKeys((prev) => {
        const next = new Set(prev);
        Object.keys(fields).forEach((k) => next.add(k));
        return next;
      });
      if (newFieldResults) {
        setFieldResults((prev) => ({ ...prev, ...newFieldResults }));
      }
      // Maestro has now actually applied fields — promote to full coloring so
      // required-and-blank fields start showing red.
      setIndicatorMode((prev) =>
        prev === 'none' || prev === 'maestro-pending' ? 'maestro' : prev
      );
    },
    []
  );

  const handleBriefProcessed = useCallback(
    () =>
      setIndicatorMode((prev) =>
        prev === 'none' || prev === 'maestro-pending' ? 'maestro' : prev
      ),
    []
  );

  const getCreatePlanFieldValue = useCallback(
    (fdKey: string) => planMetadata[fdKey],
    [planMetadata]
  );

  const onFormDataChange = useCallback(
    (data: FormData) => {
      setPlanMetadata({ ...(data.metadata || {}) });
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(() => {
        const snapshot = {
          ...data,
          metadata: { ...(data.metadata || {}) },
        };
        planningDraftService
          .putCreateDraft(entityId, snapshot as Record<string, unknown>)
          .catch(() => {
            /* non-blocking */
          });
      }, 600);
    },
    [entityId]
  );

  const handleSubmit = async (data: FormData) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        metadata: { ...data.metadata, id: entityId },
      };
      await planningService.createPlan(payload);
      setPlanSaved(true);
      setShowPlanSavedAck(true);
      void maestroRef.current?.runBriefReviewAfterCreatePlan();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      let message: string;
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail?.message) {
        message = detail.message;
      } else if (Array.isArray(detail)) {
        message = detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
      } else {
        message = err?.message || 'Failed to create media plan';
      }
      setSubmitError(message);
      // Intentionally swallowed: the banner above is the user-facing surface.
      // Rethrowing would bubble through useTemplateForm.submit's floating
      // promise as an unhandled rejection with no additional handling.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenMixerFromTimeline = () => {
    const split = flightTimeline?.metrics?.channel_split_net_cost;
    if (split && Object.keys(split).length) {
      maestroRef.current?.openMediaMixer(normalizeChannelPercents(split as Record<string, number>));
    } else {
      maestroRef.current?.openMediaMixer();
    }
  };

  return (
    <SkeletonPage
      pageTitle=""
      breadcrumbs={[{ label: 'Planning', href: '/planning' }, { label: 'Create Media Plan' }]}
      padded={false}
      contentSx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'stretch',
          flex: 1,
          width: '100%',
          minHeight: { lg: 'calc(100vh - 64px)' },
        }}
      >
        <Box
          sx={{
            flex: { lg: '1 1 50%' },
            minWidth: 0,
            maxWidth: { lg: '50%' },
            p: 3,
            pr: { lg: 2 },
            overflow: 'auto',
          }}
        >
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          {showPlanSavedAck && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              data-testid="create-plan-success"
              onClose={() => setShowPlanSavedAck(false)}
            >
              Media plan saved. Stay here to keep chatting with Maestro (it can fill fields from
              your brief) or to confirm details; use <strong>Planning</strong> in the breadcrumb
              when you want the hub.
            </Alert>
          )}

          {workspacePhase === 'form' ? (
            <>
              <Card sx={{ border: 1, borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Create Media Plan
                  </Typography>
                  {/* Pickers offer what mediaplan.create allows: the model default (ORCH-366) */}
                  <TemplateForm
                    ref={formRef}
                    template={CreateMediaPlanTemplate}
                    tenantId={tenantId}
                    entityId={entityId}
                    onSubmit={handleSubmit}
                    onChange={onFormDataChange}
                    indicatorMode={indicatorMode}
                    appliedFieldKeys={appliedFieldKeys}
                    fieldResults={fieldResults}
                  />
                </CardContent>
              </Card>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <SecondaryButton onClick={() => navigate('/planning')} disabled={isSubmitting}>
                  Cancel
                </SecondaryButton>
                <Button
                  variant="contained"
                  onClick={() => {
                    // In the no-brief path, activate validation coloring on first submit
                    // attempt so required-empty fields show red (validate-on-submit UX).
                    // In the Maestro path, a submit attempt while still 'maestro-pending'
                    // (brief attached, no Maestro output yet) also promotes to full
                    // coloring — the user is asking "what's missing" right now.
                    setIndicatorMode((prev) =>
                      prev === 'none' ? 'submitted' : prev === 'maestro-pending' ? 'maestro' : prev
                    );
                    formRef.current?.submit();
                  }}
                  disabled={isSubmitting || planSaved}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} />
                  ) : planSaved ? (
                    'Plan saved'
                  ) : (
                    'Create Plan'
                  )}
                </Button>
              </Box>
            </>
          ) : flightTimeline ? (
            <FlightTimelinePanel
              data={flightTimeline}
              onBack={() => setWorkspacePhase('form')}
              onOpenMixer={handleOpenMixerFromTimeline}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress aria-label="Loading flight workspace" />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: { lg: '0 0 50%' },
            width: { xs: '100%', lg: '50%' },
            maxWidth: { lg: '50%' },
            minWidth: 0,
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              p: 3,
              pl: { lg: 2 },
            }}
          >
            <MaestroChatPanel
              ref={maestroRef}
              planningEntityId={entityId}
              tenantId={tenantId}
              hasAttachedMediaBrief={hasAttachedMediaBrief}
              getCreatePlanFieldValue={getCreatePlanFieldValue}
              resolveContinuePastBrief={() => planSaved}
              title="Maestro"
              sx={{ border: 1, borderColor: 'divider' }}
              onApplyCreatePlanFields={handleApplyCreatePlanFields}
              onBriefProcessed={handleBriefProcessed}
              onFlightTimelineReady={(data) => {
                setFlightTimeline(data);
                setWorkspacePhase('flights');
              }}
            />
          </Box>
        </Box>
      </Box>
    </SkeletonPage>
  );
};

export default CreateMediaPlanPage;
